import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  setAccessToken,
  CURRENT_LEGAL_VERSION,
  TermsOfUseContent,
  PrivacyPolicyContent,
} from '@adthlete/shared-frontend'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PasswordInput } from '@/components/ui/password-input'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/hooks/useAuth'
import { useCreateDirectMessageThreadMutation } from '@/redux/marketerApi'
import { fetchCurrentUser } from '@/redux/userSlice'
import type { AppDispatch } from '@/store'
import api from '@/lib/api'
import { saveDraft } from './sheetMessageDraft'

// A brand messages an athlete from a talent sheet. A signed-in brand sends straight away; anyone
// else writes the message first and then creates an account to send it, so signing up comes at
// the moment they want to act. A brand who already has an account signs in, and the draft is
// kept (in sessionStorage) until they come back.

const inputClass =
  'border-foreground/15 bg-card text-foreground placeholder:text-gray-500 focus-visible:ring-[#C0FD72]/40 focus-visible:ring-offset-0'

interface SheetMessageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sheetId: string
  /** Brand the sheet was prepared for, to suggest as the company name */
  brandName?: string | null
  athlete: { id: string; name: string; avatar?: string | null }
  initialMessage?: string
}

export const SheetMessageDialog = ({
  open,
  onOpenChange,
  sheetId,
  brandName,
  athlete,
  initialMessage = '',
}: SheetMessageDialogProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const { marketer } = useAuth()
  const [createThread] = useCreateDirectMessageThreadMutation()

  const [step, setStep] = useState<'compose' | 'signup'>('compose')
  const [message, setMessage] = useState(initialMessage)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState(brandName ?? '')
  const [password, setPassword] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [error, setError] = useState('')
  const [accountExists, setAccountExists] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!open) return
    setStep('compose')
    setMessage(initialMessage)
    setError('')
    setAccountExists(false)
  }, [open, initialMessage])

  const signedIn = !!marketer?.id
  const firstName = athlete.name.split(' ')[0]

  const sendAsSignedIn = async () => {
    setSending(true)
    setError('')
    try {
      const res = await createThread({
        athleteId: athlete.id,
        message: message.trim(),
        talentSheetId: sheetId,
      }).unwrap()
      navigate(`/messages/${res.thread.id}`)
    } catch (err: any) {
      setError(err?.data?.error || 'Could not send your message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const handleCompose = () => {
    if (!message.trim()) return
    if (signedIn) sendAsSignedIn()
    else setStep('signup')
  }

  const handleSignIn = () => {
    saveDraft({ sheetId, athleteId: athlete.id, message })
    const returnTo = `${location.pathname}?compose=1`
    navigate(`/signin?returnTo=${encodeURIComponent(returnTo)}`)
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setAccountExists(false)
    setSending(true)
    try {
      const { data } = await api.post(`/public/talent-sheets/${sheetId}/outreach`, {
        name,
        email,
        password,
        company,
        athleteId: athlete.id,
        message: message.trim(),
        agreed_to_terms: agreed,
        legal_version: CURRENT_LEGAL_VERSION,
      })

      if (data.confirmationRequired) {
        navigate(`/confirmation-sent?email=${encodeURIComponent(data.email ?? email)}`)
        return
      }

      setAccessToken(data.token)
      await dispatch(fetchCurrentUser()).unwrap()
      navigate(data.threadId ? `/messages/${data.threadId}` : '/messages')
    } catch (err: any) {
      const msg: string = err?.response?.data?.error || 'Could not create your account.'
      setAccountExists(/already (exists|registered)/.test(msg))
      setError(msg)
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0c1a24] border-foreground/10 text-foreground max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={athlete.avatar ?? undefined} alt={athlete.name} />
              <AvatarFallback className="bg-muted text-foreground">
                {athlete.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="text-left">
              <DialogTitle>
                {step === 'compose' ? `Message ${athlete.name}` : 'One last step'}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {step === 'compose'
                  ? `${firstName} and their management team will get your message.`
                  : 'Create your free brand account to send this message and manage your deals.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === 'compose' ? (
          <div className="space-y-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              autoFocus
              placeholder={`Hi ${firstName}, this is [Name] from [Brand]. We loved your content and have a question before we book...`}
              className={inputClass}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <DialogFooter className="gap-2">
              <Button variant="dark-outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="lime" onClick={handleCompose} disabled={!message.trim() || sending}>
                {signedIn ? (sending ? 'Sending...' : 'Send message') : 'Continue'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="rounded-lg border border-foreground/10 bg-card p-3 text-sm text-gray-700">
              <p className="line-clamp-3 whitespace-pre-line">{message}</p>
              <button
                type="button"
                onClick={() => setStep('compose')}
                className="mt-1 text-xs text-[#3F7A0A] hover:underline"
              >
                Edit message
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="sheet-name" className="text-gray-700">
                  Your name
                </Label>
                <Input
                  id="sheet-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  className={inputClass}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sheet-company" className="text-gray-700">
                  Company
                </Label>
                <Input
                  id="sheet-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  autoComplete="organization"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sheet-email" className="text-gray-700">
                Work email
              </Label>
              <Input
                id="sheet-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className={inputClass}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sheet-password" className="text-gray-700">
                Password
              </Label>
              <PasswordInput
                id="sheet-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                className={inputClass}
              />
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="sheet-terms"
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked === true)}
                className="border-foreground/20 data-[state=checked]:bg-[#C0FD72] data-[state=checked]:border-[#C0FD72] mt-0.5"
              />
              <label htmlFor="sheet-terms" className="text-sm text-gray-700 leading-relaxed">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTerms((v) => !v)}
                  className="text-[#3F7A0A] underline hover:text-[#3F7A0A]"
                >
                  Terms of Use and Privacy Policy
                </button>
              </label>
            </div>
            {showTerms && (
              <div className="space-y-3">
                <TermsOfUseContent maxHeight="h-40" />
                <PrivacyPolicyContent maxHeight="h-40" />
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600">
                {error}{' '}
                {accountExists && (
                  <button
                    type="button"
                    onClick={handleSignIn}
                    className="text-[#3F7A0A] underline"
                  >
                    Sign in instead
                  </button>
                )}
              </p>
            )}

            <Button type="submit" variant="lime" className="w-full" disabled={sending || !agreed}>
              {sending ? 'Creating account...' : 'Create account and send'}
            </Button>
            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <button type="button" onClick={handleSignIn} className="text-[#3F7A0A] underline">
                Sign in
              </button>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
